"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
const faqData = [{
  id: "item-1",
  question: "How do City of Brookhaven and DeKalb County permits work for whole-home projects?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Whole-home renovations in Brookhaven must comply with municipal building codes and zoning ordinances administered by the City of Brookhaven Community Development Department, with specific water and sewer sign-offs handled via DeKalb County.
        </p>
        <p>
          Our team manages the entire submittal package from start to finish, including:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-foreground/90">
          <li>Architectural blueprints and structural engineer stamps</li>
          <li>Brookhaven Tree Canopy Preservation Ordinance compliance and arborist surveys</li>
          <li>DeKalb County Watershed Management approvals where plumbing footprint shifts</li>
          <li>Scheduled rough-in, insulation, and final trade inspections</li>
        </ul>
      </div>
}, {
  id: "item-2",
  question: "Can we remain living in our home during a major interior renovation?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Whether staying in place is feasible depends on the renovation scope. When projects encompass simultaneous kitchen, primary bath, and HVAC reconfigurations, most homeowners choose short-term rental arrangements during rough construction.
        </p>
        <p>
          If you choose to stay, we establish clear living containment measures:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-foreground/90">
          <li>Floor-to-ceiling zip-wall barriers with negative air pressure scrubbers</li>
          <li>Temporary kitchen setup with dedicated refrigeration and small appliance circuits</li>
          <li>An operational bathroom kept clean and accessible at all times</li>
          <li>Strict daily construction quiet hours and site cleanup protocols</li>
        </ul>
      </div>
}, {
  id: "item-3",
  question: "What is a realistic timeline for a complete Brookhaven whole-home remodel?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          A standard whole-home renovation typically spans four to nine months of active construction, preceded by two to three months of architectural planning, permitting, and finish procurement.
        </p>
        <p>
          We lock in long-lead items—such as custom cabinetry, clad windows, and specialized plumbing trim—before demolition begins to protect your on-site schedule from avoidable supply chain delays.
        </p>
      </div>
}, {
  id: "item-4",
  question: "How do you handle unforeseen structural findings and change orders?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Many Brookhaven residences built in mid-century or traditional styles conceal previous non-conforming wiring, dated cast iron plumbing, or framing shifts. When we expose an unforeseen condition, we pause that segment immediately.
        </p>
        <p>
          You receive a detailed digital assessment with photo documentation, engineering guidance, and a transparent cost and timeline revision. No additional work proceeds without your written sign-off.
        </p>
      </div>
}, {
  id: "item-5",
  question: "How do you coordinate concurrent trade scopes across kitchens, baths, and additions?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          An on-site project coordinator oversees daily staging so specialty trades—framing, mechanical, electrical, drywall, tile, and millwork—do not overlap counterproductively.
        </p>
        <p>
          Weekly owner updates detail completed milestones, upcoming county inspection dates, and immediate decisions needed so your entire remodel progresses in disciplined unison.
        </p>
      </div>
}];
export default function RemodelingFaq() {
  return <section id="remodelingfaq" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Context & Visual */}
          <div className="lg:col-span-5 flex flex-col gap-6 lg:sticky lg:top-24">
            <div className="space-y-4">
              <span className="text-xs uppercase tracking-widest text-primary font-semibold">
                Project Guidance
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight leading-tight">
                Brookhaven Whole-Home Remodeling FAQs
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Clear answers regarding local permitting, phasing logistics, site protocols, and trade coordination across Brookhaven, Georgia.
              </p>
            </div>

            <div className="relative rounded-xl overflow-hidden border border-border bg-muted shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation_interior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation_interior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation_interior.png-wvc-sizes" alt="Renovated open-concept living area and kitchen in Brookhaven Georgia featuring custom millwork and hardwood flooring" loading="lazy" decoding="async" className="w-full h-72 sm:h-80 object-cover" />
              <div className="p-5 bg-card border-t border-border">
                <p className="text-sm font-medium text-card-foreground">
                  Have a specific question about your property?
                </p>
                <p className="text-xs text-muted-foreground mt-1 mb-4">
                  We review your zoning, existing floor plan, and vision during an initial consultation.
                </p>
                <Button asChild variant="default" size="sm">
                  <Link to="/ContactRequestAnEstimatePage">
                    Request an Estimate
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Accordion */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
              {faqData.map((item, i) => <div key={item.id} data-index={i}>
                  <AccordionItem value={item.id} className="border border-border rounded-lg bg-card px-5 sm:px-6 shadow-xs data-[state=open]:border-primary/50 transition-colors">
                    <AccordionTrigger className="text-base sm:text-lg font-serif font-semibold text-foreground py-5 hover:no-underline hover:text-primary transition-colors text-left">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pt-1">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}